import { Component, Emit, Vue } from "vue-property-decorator";
import { GraphApiUtils } from '@/utils/graphApiUtils';
import { ApiResult } from '@/enums';
import { GroupUtils } from '@/utils/groupUtils';
import { AppConfig } from '@/app-config';
import { Consts } from '@/consts';
import { Groups } from '@/models/groups';
import { Group } from '@/models/group';
import { ChannelUtils } from '@/utils/channelUtils';
import { Channels } from '@/models/channels';
import { Channel } from '@/models/channel';
import { Plans } from '@/models/plans';
import { PlannerUtils } from '@/utils/plannerUtils';
import { Buckets } from '@/models/buckets';
import { Users } from '@/models/users';
import Loading from '@/components/Loading.vue';
import { DriveItem } from '@/models/driveItem';
import { DriveItems } from '@/models/driveItems';

@Component({
	components: { Loading }
})
export default class AdminManagement extends Vue {

	private groupName: string = '';
	private students: Users = new Users();
	private channels: Channels = new Channels();
	private plans: Plans = new Plans();
	private isLoadingStudents: boolean = false;
	private isLoadingPlans: boolean = false;
	private groupId: string = '';

	public async created(): Promise<void> {
		// アクセストークンが有効でない場合はルートに遷移。
		if (!await GraphApiUtils.isValidAccessToken(Vue.$accessTokenInfo)) {
			this.$router.push(Consts.PATH_ADMIN);
			return;
		}
		const userId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_ID);
		if ((userId === null) || !this.isSuperAdmin(userId)) {
			alert('この画面へのアクセス権限がありません。');
			return;
		}
		const accessToken: string = Vue.$accessTokenInfo.token;
		const groupId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_GROUPID);
		if (groupId === null) {
			return;
		}
		this.groupId = groupId;
		// Teamの取得
		const teamsResult = await GroupUtils.GetJoinedTeams(accessToken);
		if ((teamsResult.result !== ApiResult.Success) || (teamsResult.value === null)) {
			return;
		}
		const teams: Groups = teamsResult.value;
		const team: Group | null = teams.get(groupId);
		if (team === null) {
			alert('デフォルト参加チームが存在しません');
			return;
		}
		this.groupName = team.displayName;

		try {
			this.isLoadingStudents = true;
			// 生徒の取得
			await this.updateStudents(accessToken, team.id);
		} finally {
			this.isLoadingStudents = false;
		}

		try {
			this.isLoadingPlans = true;
			// Channelの取得
			await this.updateChannels(accessToken, team.id);
			// Planの取得
			await this.updatePlans(accessToken, team.id);
		} finally {
			this.isLoadingPlans = false;
		}
	}

	private isSuperAdmin(userId: string): boolean {
		for (const adminId of AppConfig.SUPER_ADMIN_IDS) {
			if (adminId === userId) {
				return true;
			}
		}
		return false;
	}

	private async updateStudents(accessToken: string, groupId: string): Promise<void> {
		try {
			this.isLoadingStudents = true;
			const studentsResult = await GroupUtils.GetBothMembers(accessToken, groupId, AppConfig.USER_SECUTIRY_GROUP_ID);
			if ((studentsResult.result !== ApiResult.Success) || (studentsResult.value === null)) {
				alert('メンバーの取得に失敗しました');
				return;
			}
			this.students = studentsResult.value;
		} finally {
			this.isLoadingStudents = false;
		}
	}

	private async updateChannels(accessToken: string, groupId: string): Promise<void> {
		const channelsResult = await ChannelUtils.GetChannels(accessToken, groupId);
		if (channelsResult.result !== ApiResult.Success) {
			alert('チャンネル一覧の取得に失敗しました');
			return;
		}
		const channels: Channels | null = channelsResult.value;
		if (channels === null) {
			return;
		}
		this.channels = channels;
	}

	private async updatePlans(accessToken: string, groupId: string): Promise<void> {
		const plansResult = await GroupUtils.GetPlans(accessToken, groupId);
		if (plansResult.result !== ApiResult.Success) {
			alert('プラン一覧の取得に失敗しました');
			return;
		}
		const plans: Plans | null = plansResult.value;
		if (plans === null) {
			return;
		}
		// チャンネル名が違う場合を除く
		this.plans = plans.isChannel(this.channels);
	}

	private async getBuckets(accessToken: string, planId: string): Promise<Buckets | null> {
		const bucketsResult = await PlannerUtils.GetBuckets(accessToken, planId);
		if (bucketsResult.result !== ApiResult.Success) {
			alert('バケット一覧の取得に失敗しました');
			return null;
		}
		const buckets: Buckets | null = bucketsResult.value;
		if (buckets === null) {
			return null;
		}
		return buckets;
	}


	private async postDriveItem(channelName: string, groupId: string): Promise<void> {

		const accessToken: string = Vue.$accessTokenInfo.token;
		// 各チャンネルのルートドライブに「管理用」と「生徒」フォルダを作成
		// const rootDrivesResult = await GroupUtils.GetRootDrives(accessToken, AppConfig.DEFAULT_GROUP_ID);
		// if (rootDrivesResult.result !== ApiResult.Success) {
		// 	alert('ルートディレクトリの取得に失敗しました');
		// 	return;
		// }
		// const drives: Drives | null = rootDrivesResult.value;
		// if (drives === null) {
		// 	return;
		// }
		// console.log(drives.value);
		const channel: Channel | null = this.channels.getChannelByName(channelName);
		if (channel === null) {
			return;
		}
		const channelFolderName: string = AppConfig.CHANNEL_ID_TO_CHNNAEL_FOLDERNAME[channel.id];
		await this.postOperationFolder(accessToken, groupId, channelFolderName);
		await this.postStudentFolder(accessToken, groupId, channelFolderName, this.students);
	}

	private async postOperationFolder(accessToken: string, groupId: string, channelFolderName: string): Promise<void> {
		// チャンネルフォルダを取得
		const channelDriveItemResult = await GroupUtils.GetDriveItemByPath(
			accessToken,
			groupId,
			channelFolderName);
		if (channelDriveItemResult.result !== ApiResult.Success) {
			alert('チャンネルフォルダの取得に失敗しました');
			return;
		}
		const channelDriveItem: DriveItem | null = channelDriveItemResult.value;
		if (channelDriveItem === null) {
			return;
		}
		// チャンネルフォルダ以下のドライブアイテムを取得
		const channelRootDriveItemsResult = await GroupUtils.GetDriveItemsByPath(
			accessToken,
			groupId,
			channelFolderName);
		if (channelRootDriveItemsResult.result !== ApiResult.Success) {
			alert('チャンネルフォルダ以下のドライブアイテムの取得に失敗しました');
			return;
		}
		const channelRootDriveItems: DriveItems | null = channelRootDriveItemsResult.value;
		if (channelRootDriveItems === null) {
			return;
		}
		const rootFolders: DriveItems = channelRootDriveItems.getFolders();
		// 「運営」フォルダを作成
		if (!rootFolders.exists(AppConfig.ADMIN_ROOT_FOLDER_NAME)) {
			const driveItemResult = await GroupUtils.PostFolder(
				accessToken,
				groupId,
				channelDriveItem.id,
				AppConfig.ADMIN_ROOT_FOLDER_NAME);
			if ((driveItemResult.result === ApiResult.Success) && (driveItemResult.value !== null)) {
				rootFolders.value.push(driveItemResult.value);
				console.log(`${AppConfig.ADMIN_ROOT_FOLDER_NAME}を新規作成しました`);
			}
		}
		// 「運営」フォルダ以下のフォルダ作成
		const operationFolder: DriveItem | null = rootFolders.getByName(AppConfig.ADMIN_ROOT_FOLDER_NAME);
		if (operationFolder === null) {
			alert(`「${AppConfig.ADMIN_ROOT_FOLDER_NAME}」フォルダが存在しません`);
			return;
		}
		await this.postBucketFolder(accessToken, groupId, operationFolder.id);
	}

	private async postStudentFolder(
		accessToken: string,
		groupId: string,
		channelFolderName: string,
		students: Users): Promise<void> {
		// チャンネルフォルダを取得
		const channelDriveItemResult = await GroupUtils.GetDriveItemByPath(
			accessToken,
			groupId,
			channelFolderName);
		if (channelDriveItemResult.result !== ApiResult.Success) {
			alert('チャンネルフォルダの取得に失敗しました');
			return;
		}
		const channelDriveItem: DriveItem | null = channelDriveItemResult.value;
		if (channelDriveItem === null) {
			return;
		}
		// チャンネルフォルダ以下のドライブアイテムを取得
		const channelRootDriveItemsResult = await GroupUtils.GetDriveItemsByPath(
			accessToken,
			groupId,
			channelFolderName);
		if (channelRootDriveItemsResult.result !== ApiResult.Success) {
			alert('チャンネルフォルダ以下のドライブアイテムの取得に失敗しました');
			return;
		}
		const channelRootDriveItems: DriveItems | null = channelRootDriveItemsResult.value;
		if (channelRootDriveItems === null) {
			return;
		}
		const rootFolders: DriveItems = channelRootDriveItems.getFolders();
		// 「生徒」フォルダを作成
		if (!rootFolders.exists(AppConfig.USER_ROOT_FOLDER_NAME)) {
			const driveItemResult = await GroupUtils.PostFolder(
				accessToken,
				groupId,
				channelDriveItem.id,
				AppConfig.USER_ROOT_FOLDER_NAME);
			if ((driveItemResult.result === ApiResult.Success) && (driveItemResult.value !== null)) {
				rootFolders.value.push(driveItemResult.value);
				console.log(`${AppConfig.USER_ROOT_FOLDER_NAME}を新規作成しました`);
			}
		}
		const rootFolder: DriveItem | null = rootFolders.getByName(AppConfig.USER_ROOT_FOLDER_NAME);
		if (rootFolder === null) {
			alert(`「${AppConfig.USER_ROOT_FOLDER_NAME}」フォルダが存在しません`);
			return;
		}
		const subRootDriveItemsResult = await GroupUtils.GetDriveItems(
			accessToken,
			groupId,
			rootFolder.id);
		if (subRootDriveItemsResult.result !== ApiResult.Success) {
			alert(`「${AppConfig.USER_ROOT_FOLDER_NAME}」フォルダ以下のドライブ取得に失敗しました`);
			return;
		}
		const subRootDriveItems: DriveItems | null = subRootDriveItemsResult.value;
		if (subRootDriveItems === null) {
			return;
		}
		const studentFolders = subRootDriveItems.getFolders();
		for (const student of students.value) {
			// 「ユーザー」フォルダ作成（フォルダ名は「名前_id」）
			const folderName: string = `${student.displayName}_${student.id}`;
			let studentFolder: DriveItem | null = studentFolders.getByName(folderName);
			if (studentFolder === null) {
				const postFolderResult = await GroupUtils.PostFolder(
					accessToken,
					groupId,
					rootFolder.id,
					folderName);
				console.log(`${folderName}を新規作成しました`);
				if ((postFolderResult.result !== ApiResult.Success) || (postFolderResult.value === null)) {
					alert(`「${folderName}」フォルダの作成に失敗しました`);
					continue;
				}
				studentFolder = postFolderResult.value;
			}
			// 「ユーザー」フォルダ以下に「バケット」フォルダ作成
			await this.postBucketFolder(accessToken, groupId, studentFolder.id);
			const bucketFolders = await this.getFolders(accessToken, groupId, studentFolder.id);
			if (bucketFolders === null) {
				continue;
			}
			// 「バケット」フォルダ以下に「タスク」フォルダを作成
			const preTaskFolder = bucketFolders.getByName(AppConfig.BUCKET_NAME_PRETASK);
			if (preTaskFolder !== null) {
				await this.postUserTaskFolder(accessToken, groupId, preTaskFolder.id);
			}
			const preTask2Folder = bucketFolders.getByName(AppConfig.BUCKET_NAME_PRETASK2);
			if (preTask2Folder !== null) {
				await this.postUserTaskFolder(accessToken, groupId, preTask2Folder.id);
			}
			const preTask3Folder = bucketFolders.getByName(AppConfig.BUCKET_NAME_PRETASK3);
			if (preTask3Folder !== null) {
				await this.postUserTaskFolder(accessToken, groupId, preTask3Folder.id);
			}
			const postTaskFolder = bucketFolders.getByName(AppConfig.BUCKET_NAME_POSTTASK);
			if (postTaskFolder !== null) {
				await this.postUserTaskFolder(accessToken, groupId, postTaskFolder.id);
			}
			console.log(bucketFolders);
		}
	}

	private async getFolders(accessToken: string, groupId: string, parentFolderId: string): Promise<DriveItems | null> {
		const driveItemsResult = await GroupUtils.GetDriveItems(
			accessToken,
			groupId,
			parentFolderId);
		if (driveItemsResult.result !== ApiResult.Success) {
			alert('フォルダ以下のドライブアイテムの取得に失敗しました');
			return null;
		}
		const driveItems: DriveItems | null = driveItemsResult.value;
		if (driveItems === null) {
			return null;
		}
		const folders: DriveItems = driveItems.getFolders();
		return folders;
	}

	private async postBucketFolder(accessToken: string, groupId: string, parentFolderId: string): Promise<void> {
		const folders = await this.getFolders(accessToken, groupId, parentFolderId);
		if (folders === null) {
			return;
		}
		// 「事前課題」フォルダ作成
		if (!folders.exists(AppConfig.BUCKET_NAME_PRETASK)) {
			await GroupUtils.PostFolder(
				accessToken,
				groupId,
				parentFolderId,
				AppConfig.BUCKET_NAME_PRETASK);
			console.log(`${AppConfig.BUCKET_NAME_PRETASK}を新規作成しました`);
		}
		// 「事前課題２」フォルダ作成
		if (!folders.exists(AppConfig.BUCKET_NAME_PRETASK2)) {
			await GroupUtils.PostFolder(
				accessToken,
				groupId,
				parentFolderId,
				AppConfig.BUCKET_NAME_PRETASK2);
			console.log(`${AppConfig.BUCKET_NAME_PRETASK2}を新規作成しました`);
		}
		if (!folders.exists(AppConfig.BUCKET_NAME_PRETASK3)) {
			await GroupUtils.PostFolder(
				accessToken,
				groupId,
				parentFolderId,
				AppConfig.BUCKET_NAME_PRETASK3);
			console.log(`${AppConfig.BUCKET_NAME_PRETASK3}を新規作成しました`);
		}
		// 「講義」フォルダ作成
		if (!folders.exists(AppConfig.BUCKET_NAME_LECTURE)) {
			await GroupUtils.PostFolder(
				accessToken,
				groupId,
				parentFolderId,
				AppConfig.BUCKET_NAME_LECTURE);
			console.log(`${AppConfig.BUCKET_NAME_LECTURE}を新規作成しました`);
		}
		// 「事後課題」フォルダ作成
		if (!folders.exists(AppConfig.BUCKET_NAME_POSTTASK)) {
			await GroupUtils.PostFolder(
				accessToken,
				groupId,
				parentFolderId,
				AppConfig.BUCKET_NAME_POSTTASK);
			console.log(`${AppConfig.BUCKET_NAME_POSTTASK}を新規作成しました`);
		}
	}

	private async postUserTaskFolder(accessToken: string, groupId: string, parentFolderId: string): Promise<void> {
		const driveItemsResult = await GroupUtils.GetDriveItems(
			accessToken,
			groupId,
			parentFolderId);
		if (driveItemsResult.result !== ApiResult.Success) {
			alert('フォルダ以下のドライブアイテムの取得に失敗しました');
			return;
		}
		const driveItems: DriveItems | null = driveItemsResult.value;
		if (driveItems === null) {
			return;
		}
		const folders = driveItems.getFolders();
		// 「提出レポート」フォルダ作成
		if (!folders.exists(AppConfig.USER_TASK_FOLDERNAME_SUBMITREPORT)) {
			await GroupUtils.PostFolder(
				accessToken,
				groupId,
				parentFolderId,
				AppConfig.USER_TASK_FOLDERNAME_SUBMITREPORT);
			console.log(`${AppConfig.USER_TASK_FOLDERNAME_SUBMITREPORT}を新規作成しました`);
		}
		// 「採点結果」フォルダ作成
		if (!folders.exists(AppConfig.USER_TASK_FOLDERNAME_CHECKSCORE)) {
			await GroupUtils.PostFolder(
				accessToken,
				groupId,
				parentFolderId,
				AppConfig.USER_TASK_FOLDERNAME_CHECKSCORE);
			console.log(`${AppConfig.USER_TASK_FOLDERNAME_CHECKSCORE}を新規作成しました`);
		}
	}

	private async postBucket(planId: string): Promise<void> {
		// console.log(planId);
		const accessToken: string = Vue.$accessTokenInfo.token;
		const buckets = await this.getBuckets(accessToken, planId);
		if (buckets === null) {
			return;
		}
		// console.log(buckets.value);
		// 事前課題
		if (!buckets.exists(AppConfig.BUCKET_NAME_PRETASK)) {
			await PlannerUtils.PostBucket(accessToken, planId, AppConfig.BUCKET_NAME_PRETASK);
			console.log(`${AppConfig.BUCKET_NAME_PRETASK}を新規作成しました`);
		}
		// 事前課題２
		if (!buckets.exists(AppConfig.BUCKET_NAME_PRETASK2)) {
			await PlannerUtils.PostBucket(accessToken, planId, AppConfig.BUCKET_NAME_PRETASK2);
			console.log(`${AppConfig.BUCKET_NAME_PRETASK2}を新規作成しました`);
		}
		// 事前課題３
		if (!buckets.exists(AppConfig.BUCKET_NAME_PRETASK3)) {
			await PlannerUtils.PostBucket(accessToken, planId, AppConfig.BUCKET_NAME_PRETASK3);
			console.log(`${AppConfig.BUCKET_NAME_PRETASK3}を新規作成しました`);
		}
		// 講義
		if (!buckets.exists(AppConfig.BUCKET_NAME_LECTURE)) {
			await PlannerUtils.PostBucket(accessToken, planId, AppConfig.BUCKET_NAME_LECTURE);
			console.log(`${AppConfig.BUCKET_NAME_LECTURE}を新規作成しました`);
		}
		// 事後課題
		if (!buckets.exists(AppConfig.BUCKET_NAME_POSTTASK)) {
			await PlannerUtils.PostBucket(accessToken, planId, AppConfig.BUCKET_NAME_POSTTASK);
			console.log(`${AppConfig.BUCKET_NAME_POSTTASK}を新規作成しました`);
		}
		alert('バケットを新規作成処理が完了しました');
	}
}
